import { FC, useState } from 'react';
import { useVesselStore } from '../../../context/VesselContext';
import { AolDataItem, AolGroup } from '../../../types/ViewModel/AolItem';
import StatusIcon from '../../ui/status-icon/NoonReportStatusIcon';
import {
    AolHeaderWrapper,
    TabButton,
    TabText,
    ButtonWrapper,
    ConfirmDeleteAol,
    RightPanelWrapper,
    MrvVerifiedIcon,
    LeftPanelWrapper,
} from './AolMain.style';
import usePostUpdateAolSection from '../../../api/usePostUpdateAolSection';
import { useAolStore } from '../../../context/AolContext';
import { useAuthStore } from '../../../context/AuthContext';
import AolMetaIconsComponent from '../aol-meta-icons/AolMetaIcons';
import { PageType } from '../../../types/Page';
import { TabsWrapper } from '../aol-list-component/AolListComponent.style';
import useAolSelectionHelper from '../../../utils/useAolSelectionHelper';
import { TabLineButton } from '../../../styles/button.style';
import AolUpdateInfo from './AolUpdateInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { hasBeenMrvVerified } from 'src/utils/aolSectionHelper';
import { AolPageEnum } from 'src/enums/aol/AolPageEnum';
import { getAolTabInfo, getTimelineText } from '../AolUtils';

type Props = {
    page: PageType;
    selectedAolGroup: AolGroup;
    selectedAolDataItem: AolDataItem;
};

const AolMainHeaderComponent: FC<Props> = (props) => {
    const { selectedVessel } = useVesselStore();
    const {
        activeAolPage,
        isSummaryVisible,
        setIsSummaryVisible,
        isCommentsVisible,
        setIsCommentsVisible,
        isEditGloballyMode,
        setIsEditGloballyMode,
    } = useAolStore();
    const { deleteAol } = usePostUpdateAolSection();
    const { role } = useAuthStore();
    const { onSelectAolDataItem } = useAolSelectionHelper();
    const [showConfirm, setShowConfirm] = useState(false);

    const confirmDeleteAol = () => {
        setShowConfirm(true);
    };

    const confirmedDeleteAol = () => {
        setShowConfirm(false);
        if (selectedVessel) {
            deleteAol(selectedVessel.imoNumber, props.selectedAolDataItem!.noonReport!.metadata.noonReportId);
        }
    };

    const cancelDeleteAol = () => {
        setShowConfirm(false);
    };

    const onApprovalPage = () => {
        return activeAolPage === AolPageEnum.Approved;
    };

    const hasReaderPermissionOnly = () => {
        return role && !role.isWriter;
    };

    const hasEditPermission = () => {
        return role && role.canEditGlobally;
    };

    const selectAolItemTab = (aolDataItem: AolDataItem) => {
        onSelectAolDataItem(aolDataItem);
    };

    return (
        <AolHeaderWrapper>
            {props.selectedAolGroup && props.selectedAolDataItem && (
                <LeftPanelWrapper>
                    <TabsWrapper>
                        {props.selectedAolGroup.aolDataItems.map((aolDataItem) => {
                            return (
                                <TabButton
                                    isSelected={aolDataItem.id === props.selectedAolDataItem.id}
                                    noonReportType={aolDataItem.reportType}
                                    isValid={aolDataItem.isValid}
                                    onClick={() => selectAolItemTab(aolDataItem)}
                                    key={aolDataItem.id}
                                >
                                    <TabText>
                                        <div>{getAolTabInfo(aolDataItem)}</div>
                                        <div>{getTimelineText(aolDataItem)}</div>
                                    </TabText>
                                    <AolMetaIconsComponent
                                        aolVersion={aolDataItem.aolVersion}
                                        hasTankersEventMismatch={aolDataItem.aolValidationItems ? true : false}
                                        isMrvVerified={null}
                                        isValid={aolDataItem.isValid}
                                    />
                                    <StatusIcon reportStatus={aolDataItem.status} />
                                </TabButton>
                            );
                        })}
                    </TabsWrapper>
                    {hasBeenMrvVerified(selectedVessel?.mrvVerifiedUntilDate, props.selectedAolGroup.aolDate) && (
                        <MrvVerifiedIcon>
                            <FontAwesomeIcon className="status-is-mrv-verified" icon="certificate" />
                            <span>Verified by DNV</span>
                        </MrvVerifiedIcon>
                    )}
                </LeftPanelWrapper>
            )}
            <RightPanelWrapper>
                {props.selectedAolDataItem && <AolUpdateInfo aolDataItem={props.selectedAolDataItem}></AolUpdateInfo>}
                <ButtonWrapper>
                    {props.page === 'reporting' && !hasReaderPermissionOnly() && props.selectedAolDataItem?.noonReport && selectedVessel && (
                        <>
                            <TabLineButton onClick={confirmDeleteAol}>Delete</TabLineButton>

                            {showConfirm && (
                                <ConfirmDeleteAol>
                                    <h5>Delete this AOL?</h5>
                                    {props.selectedAolDataItem?.aolVersion && props.selectedAolDataItem?.aolVersion >= 5 && (
                                        <p>This will also delete the ROB data related to the AoL item.</p>
                                    )}
                                    <div>
                                        <button onClick={cancelDeleteAol}>Cancel</button>
                                        <button onClick={confirmedDeleteAol}>Delete</button>
                                    </div>
                                </ConfirmDeleteAol>
                            )}
                        </>
                    )}
                    {hasEditPermission() && onApprovalPage() && (
                        <TabLineButton isSelected={isEditGloballyMode} onClick={() => setIsEditGloballyMode(!isEditGloballyMode)}>
                            Edit
                        </TabLineButton>
                    )}
                    <TabLineButton isSelected={isCommentsVisible} onClick={() => setIsCommentsVisible(!isCommentsVisible)}>
                        <FontAwesomeIcon icon="comment" title={'Show comments'} />
                    </TabLineButton>
                    <TabLineButton isSelected={isSummaryVisible} onClick={() => setIsSummaryVisible(!isSummaryVisible)}>
                        <FontAwesomeIcon icon="list-ol" title={'Show summary'} />
                    </TabLineButton>
                </ButtonWrapper>
            </RightPanelWrapper>
        </AolHeaderWrapper>
    );
};

export default AolMainHeaderComponent;
